import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Host,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { MaterialItemComposerDirective } from '../material-item-composer-directive';
import { MaterialsManagerCoreComponent } from '../materials-manager-core.component';
import { BehaviorSubject } from 'rxjs';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { Utils } from '@klickdata/core/util';
import { ResourceItemData } from '@klickdata/core/resource-item';
import { MaterialManagerCoreService } from '../material-manager-core.service';

@Component({
    selector: 'app-materials-manager-text',
    templateUrl: './materials-manager-text.component.html',
    styleUrls: ['./materials-manager-text.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class MaterialsManagerTextComponent extends MaterialItemComposerDirective implements AfterViewInit {
    public splitMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public generatedBlocks = 0;
    public currentSelectedText: string;
    @ViewChild('editableDiv') editableDiv!: ElementRef<HTMLElement>;
    @Output() onSplitText = new EventEmitter<{ blocksCount?: number; index?: number }>();
    @Output() onAddNewItem = new EventEmitter<{ item: ResourceItemData; openItem?: boolean; index?: number }>();
    constructor(
        protected cdRef: ChangeDetectorRef,
        protected messageService: MessageService,
        protected parentFormDirective: FormGroupDirective,
        protected materialManagerCoreService: MaterialManagerCoreService
    ) {
        super(cdRef, parentFormDirective, materialManagerCoreService);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.focusEditor();
        }, 2000);
    }
    focusEditor() {
        const editors = document
            .getElementsByClassName('basic-tinymce-editor-ele')
            [this.index]?.getElementsByTagName('iframe');

        if (editors !== undefined && editors.length > 0) {
            const editor = editors[0];
            const body = editor.contentDocument.getElementsByTagName('body');
            if (body !== undefined && body.length > 0) {
                body[0].focus();
            }
        }
    }
    splitText(useAi: boolean, blocksCount?: number, index?: number): void {
        if (useAi) {
            this.onSplitText.emit({ blocksCount, index });
        } else {
            this.splitMode.next(true);
        }
    }
    onTextSelection(editableDiv: HTMLElement): void {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            this.currentSelectedText = null;
            return;
        }

        // Ensure the selection is within the editableDiv
        const range = selection.getRangeAt(0);
        if (!editableDiv.contains(range.commonAncestorContainer)) {
            this.currentSelectedText = null;
            return;
        }

        // Create a temporary container to hold the selected HTML
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(range.cloneContents());
        this.currentSelectedText = tempDiv.innerHTML;

        // if (selection && selection.rangeCount > 0) {
        //     const range = selection.getRangeAt(0);
        //     if (editableDiv.contains(range.commonAncestorContainer)) {
        //         this.currentSelectedText = selection.toString();
        //     }
        // }
    }

    onDownload(item: FormGroup) {
        const cleanHtml = this.cleanHtmlContent(item.value.name);

        // Create clean HTML structure with the preserved content
        const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Downloaded Text</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 20px;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
        }
        h1, h2, h3, h4, h5, h6 {
            margin-top: 1.5em;
            margin-bottom: 0.5em;
            font-weight: bold;
        }
        h1 { font-size: 2em; }
        h2 { font-size: 1.5em; }
        h3 { font-size: 1.25em; }
        h4 { font-size: 1.1em; }
        h5 { font-size: 1em; }
        h6 { font-size: 0.9em; }
        p {
            margin-bottom: 1em;
        }
        ul, ol {
            margin-bottom: 1em;
            padding-left: 2em;
        }
        li {
            margin-bottom: 0.5em;
        }
        strong, b {
            font-weight: bold;
        }
        em, i {
            font-style: italic;
        }
        blockquote {
            border-left: 4px solid #ccc;
            margin: 1em 0;
            padding-left: 1em;
            font-style: italic;
        }
    </style>
</head>
<body>
    ${cleanHtml}
</body>
</html>`;

        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${item.value.title}.html`;
        a.click();
        URL.revokeObjectURL(url);
    }

    private cleanHtmlContent(html: string): string {
        // Create a temporary div to work with the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Remove unwanted elements and attributes
        const unwantedElements = tempDiv.querySelectorAll('script, style, link, meta, title, head, body, html');
        unwantedElements.forEach((el) => el.remove());

        // Remove unwanted attributes but keep important ones
        const allElements = tempDiv.querySelectorAll('*');
        allElements.forEach((el) => {
            // Keep only essential attributes
            const allowedAttributes = ['href', 'src', 'alt', 'title'];
            const attributes = Array.from(el.attributes);
            attributes.forEach((attr) => {
                if (!allowedAttributes.includes(attr.name)) {
                    el.removeAttribute(attr.name);
                }
            });
        });

        // Clean up empty elements
        const emptyElements = tempDiv.querySelectorAll('div:empty, span:empty, p:empty');
        emptyElements.forEach((el) => el.remove());

        return tempDiv.innerHTML;
    }
    onSplit(editableDiv: HTMLElement) {
        if (this.generatedBlocks == 1) {
            // this.opened = this.index;
            this.duplicateItem(this.index);
        }
        this.onAddNewItem.emit({
            item: {
                item_type_value: 'text_material',
                name: this.currentSelectedText,
                title: Utils.getHeadline(this.currentSelectedText),
            },
            openItem: false,
            index: this.index + 1,
        });
        this.messageService.openMessage(
            MessageSavedComponent,
            $localize`New material item created with the selected text`
        );
        this.removeSelectedText(editableDiv);
        this.currentSelectedText = null;
    }
    removeSelectedText(editableDiv: HTMLElement): void {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            // Ensure the selection is within the editable div
            if (editableDiv.contains(range.commonAncestorContainer)) {
                // Remove the selected content
                range.deleteContents();
                this.itemForm.get('name')?.setValue(editableDiv.innerHTML);
            }
        }
    }
    onDoneSplitting() {
        this.splitMode.next(false);
        this.onDisableSorting.emit(false);
        // if (this.generatedBlocks > 0) {
        //     this.deleteItem(this.index);
        // }
    }
}
